/**
 * @fileoverview React hook for Facebook Pages Connect operations.
 * @module facebook-pages-connect/hooks/useFacebookPagesConnect
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFacebookPagesConnectService } from '../services/FacebookPagesConnectService';
import type {
  FacebookPageSummary,
  FacebookPagesConnectionStatus,
  FacebookPagesPublicationStatus,
  FacebookPagesPublishRequest,
} from '../types';

const OAUTH_SESSION_KEY = 'facebook_pages_oauth_in_progress';
const OAUTH_RETURN_TO_KEY = 'facebook_pages_oauth_return_to';
const OAUTH_RETURN_RESTORED_KEY = 'facebook_pages_oauth_return_restored';

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function stripFacebookPagesCallbackParams(): void {
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of ['facebookPages', 'error']) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (!changed) return;
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

function buildCallbackRestoredReturnTo(
  returnTo: string,
  sourceUrl: URL,
): string {
  const restored = new URL(returnTo, window.location.origin);
  for (const key of ['facebookPages', 'error']) {
    const value = sourceUrl.searchParams.get(key);
    if (value) {
      restored.searchParams.set(key, value);
    }
  }
  return `${restored.pathname}${restored.search}${restored.hash}`;
}

export interface UseFacebookPagesConnectResult {
  status: FacebookPagesConnectionStatus | null;
  pages: FacebookPageSummary[];
  selectedPage: FacebookPageSummary | null;
  publishResult: FacebookPagesPublicationStatus | null;
  isConnected: boolean;
  loading: boolean;
  actionLoading: boolean;
  pagesLoading: boolean;
  publishing: boolean;
  readbackLoading: boolean;
  error: string | null;
  refreshStatus: () => Promise<void>;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  refreshPages: () => Promise<void>;
  selectPage: (pageId: string) => Promise<void>;
  publish: (request: Omit<FacebookPagesPublishRequest, 'projectId'>) => Promise<FacebookPagesPublicationStatus | null>;
  readback: (postId: string, context?: { strategyId?: string; publicationId?: string }) => Promise<FacebookPagesPublicationStatus | null>;
  clearPublishResult: () => void;
}

export function useFacebookPagesConnect(projectId: string): UseFacebookPagesConnectResult {
  const [status, setStatus] = useState<FacebookPagesConnectionStatus | null>(null);
  const [pages, setPages] = useState<FacebookPageSummary[]>([]);
  const [publishResult, setPublishResult] = useState<FacebookPagesPublicationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [readbackLoading, setReadbackLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const statusAbortRef = useRef<AbortController | null>(null);
  const statusRef = useRef<FacebookPagesConnectionStatus | null>(null);
  const processedCallbackRef = useRef(false);
  const mountedRef = useRef(true);

  const service = useMemo(() => createFacebookPagesConnectService(), []);
  const selectedPage = status?.selectedPage ?? null;

  const refreshStatus = useCallback(async () => {
    if (!projectId) return;

    statusAbortRef.current?.abort();
    statusAbortRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);
      const nextStatus = await service.getStatus(projectId, statusAbortRef.current.signal);
      if (mountedRef.current) {
        statusRef.current = nextStatus;
        setStatus(nextStatus);
        setPages(nextStatus.accessiblePages || []);
      }
    } catch (err) {
      if (isAbortError(err)) return;
      if (mountedRef.current) {
        statusRef.current = { connected: false };
        setError(err instanceof Error ? err.message : 'Failed to load Facebook Pages connection status');
        setStatus({ connected: false });
        setPages([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [projectId, service]);

  const refreshStatusWithRetries = useCallback(async () => {
    const delaysMs = [0, 400, 1200, 2500];
    for (const delayMs of delaysMs) {
      if (!mountedRef.current) return;
      if (delayMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs));
      }
      await refreshStatus();
      if (statusRef.current?.connected) {
        return;
      }
    }
  }, [refreshStatus]);

  const connect = useCallback(async () => {
    if (!projectId) return;

    try {
      setActionLoading(true);
      setError(null);
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const oauthUrl = await service.getOAuthUrl(projectId, returnTo);
      sessionStorage.setItem(OAUTH_SESSION_KEY, projectId);
      sessionStorage.setItem(OAUTH_RETURN_TO_KEY, returnTo);
      sessionStorage.removeItem(OAUTH_RETURN_RESTORED_KEY);
      window.location.href = oauthUrl;
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to connect Facebook Pages');
        setActionLoading(false);
      }
    }
  }, [projectId, service]);

  const disconnect = useCallback(async () => {
    if (!projectId) return;

    try {
      setActionLoading(true);
      setError(null);
      setPublishResult(null);
      const nextStatus = await service.disconnect(projectId);
      if (mountedRef.current) {
        statusRef.current = nextStatus;
        setStatus(nextStatus);
        setPages([]);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to disconnect Facebook Pages');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setActionLoading(false);
      }
    }
  }, [projectId, service]);

  const refreshPages = useCallback(async () => {
    if (!projectId) return;

    try {
      setPagesLoading(true);
      setError(null);
      const result = await service.listPages(projectId);
      if (mountedRef.current) {
        setPages(result.pages);
        setStatus(prev => {
          const nextStatus = prev
          ? { ...prev, accessiblePages: result.pages, selectedPage: result.selectedPage }
          : { connected: true, accessiblePages: result.pages, selectedPage: result.selectedPage };
          statusRef.current = nextStatus;
          return nextStatus;
        });
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to refresh Facebook Pages');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setPagesLoading(false);
      }
    }
  }, [projectId, service]);

  const selectPage = useCallback(async (pageId: string) => {
    if (!projectId) return;

    try {
      setActionLoading(true);
      setError(null);
      const result = await service.selectPage(projectId, pageId);
      if (mountedRef.current) {
        setStatus(prev => {
          const nextStatus = prev
          ? { ...prev, selectedPage: result.selectedPage, updatedAt: result.updatedAt }
          : { connected: true, selectedPage: result.selectedPage, updatedAt: result.updatedAt };
          statusRef.current = nextStatus;
          return nextStatus;
        });
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to select Facebook Page');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setActionLoading(false);
      }
    }
  }, [projectId, service]);

  const publish = useCallback(async (
    request: Omit<FacebookPagesPublishRequest, 'projectId'>,
  ): Promise<FacebookPagesPublicationStatus | null> => {
    if (!projectId) return null;

    try {
      setPublishing(true);
      setError(null);
      const result = await service.publish({ projectId, ...request });
      if (mountedRef.current) {
        setPublishResult(result);
      }
      return result;
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to publish to Facebook Page');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setPublishing(false);
      }
    }
  }, [projectId, service]);

  const readback = useCallback(async (
    postId: string,
    context: { strategyId?: string; publicationId?: string } = {},
  ): Promise<FacebookPagesPublicationStatus | null> => {
    if (!projectId) return null;

    try {
      setReadbackLoading(true);
      setError(null);
      const result = await service.readback({ projectId, postId, ...context });
      if (mountedRef.current) {
        setPublishResult(result);
      }
      return result;
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to read Facebook Page post status');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setReadbackLoading(false);
      }
    }
  }, [projectId, service]);

  useEffect(() => {
    mountedRef.current = true;
    refreshStatus();

    return () => {
      mountedRef.current = false;
      statusAbortRef.current?.abort();
    };
  }, [refreshStatus]);

  useEffect(() => {
    if (!projectId || processedCallbackRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const callbackStatus = params.get('facebookPages');
    const callbackProjectId = sessionStorage.getItem(OAUTH_SESSION_KEY);
    const storedReturnTo = sessionStorage.getItem(OAUTH_RETURN_TO_KEY);
    const restoredOnce = sessionStorage.getItem(OAUTH_RETURN_RESTORED_KEY) === '1';
    if (!callbackStatus || callbackProjectId !== projectId) return;

    const currentUrl = new URL(window.location.href);
    if (!restoredOnce && storedReturnTo) {
      const restoredReturnTo = buildCallbackRestoredReturnTo(storedReturnTo, currentUrl);
      const currentPath = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
      if (restoredReturnTo !== currentPath) {
        sessionStorage.setItem(OAUTH_RETURN_RESTORED_KEY, '1');
        window.location.replace(restoredReturnTo);
        return;
      }
    }

    processedCallbackRef.current = true;
    sessionStorage.removeItem(OAUTH_SESSION_KEY);
    sessionStorage.removeItem(OAUTH_RETURN_TO_KEY);
    sessionStorage.removeItem(OAUTH_RETURN_RESTORED_KEY);
    if (callbackStatus === 'connected') {
      void refreshStatusWithRetries();
    } else if (callbackStatus === 'error') {
      setError(params.get('error') || 'Facebook Pages connection failed');
    }
    stripFacebookPagesCallbackParams();
  }, [projectId, refreshStatusWithRetries]);

  useEffect(() => {
    if (!projectId) return;

    const handleFocus = () => {
      void refreshStatus();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshStatus();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [projectId, refreshStatus]);

  return {
    status,
    pages,
    selectedPage,
    publishResult,
    isConnected: Boolean(status?.connected),
    loading,
    actionLoading,
    pagesLoading,
    publishing,
    readbackLoading,
    error,
    refreshStatus,
    connect,
    disconnect,
    refreshPages,
    selectPage,
    publish,
    readback,
    clearPublishResult: () => setPublishResult(null),
  };
}

export default useFacebookPagesConnect;
